import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { adminLogin, fetchAdminLeads, fetchAdminStats, adminLogout } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Flame, ThermometerSnowflake, Snowflake, Inbox } from "lucide-react";

const TOKEN_KEY = "am_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchAdminLeads(t), fetchAdminStats(t)]);
      setLeads(l.leads); setStats(s);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(""); toast.error("Session expired"); }
      else toast.error("Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { token: t } = await adminLogin(creds.username, creds.password);
      localStorage.setItem(TOKEN_KEY, t); setToken(t);
      toast.success("Welcome back");
    } catch { toast.error("Invalid credentials"); }
  };

  const logout = async () => {
    try { await adminLogout(token); } catch (_) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setLeads([]); setStats(null);
  };

  if (!token) {
    return (
      <div className="min-h-[80vh] bg-[#F8FAFC] flex items-center justify-center px-6">
        <Card className="w-full max-w-md rounded-3xl p-8 border-[#E2E8F0] bg-white">
          <h1 className="font-heading text-2xl font-bold text-[#0B132B]">Admin Login</h1>
          <p className="text-sm text-[#475569] mt-1">View and manage AeroMax leads.</p>
          <form onSubmit={login} className="mt-6 space-y-4">
            <div><Label className="text-xs">Username</Label><Input data-testid="admin-user" value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} /></div>
            <div><Label className="text-xs">Password</Label><Input data-testid="admin-pass" type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} /></div>
            <Button data-testid="admin-login-btn" type="submit" className="w-full rounded-full bg-[#0B132B] hover:bg-[#050914] text-white py-5 font-semibold">Sign in</Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-3xl font-bold text-[#0B132B]">Leads Dashboard</h1>
          <Button onClick={logout} variant="outline" className="rounded-full border-[#E2E8F0]"><LogOut className="h-4 w-4 mr-2" />Sign out</Button>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Inbox} label="Total leads" value={stats.total} color="text-[#0B132B]" />
            <StatCard icon={Flame} label="Hot" value={stats.hot} color="text-[#EF4444]" />
            <StatCard icon={ThermometerSnowflake} label="Warm" value={stats.warm} color="text-[#F59E0B]" />
            <StatCard icon={Snowflake} label="Cold" value={stats.cold} color="text-[#2563EB]" />
          </div>
        )}

        <Card className="mt-8 rounded-2xl p-6 border-[#E2E8F0] bg-white">
          {loading ? <p className="text-sm text-[#475569]">Loading…</p> : leads.length === 0 ? (
            <p className="text-sm text-[#475569]" data-testid="admin-empty">No leads yet.</p>
          ) : (
            <div className="overflow-x-auto" data-testid="admin-leads-table">
              <table className="w-full text-sm">
                <thead className="text-left text-[#475569]">
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="py-3 pr-4">When</th><th className="py-3 pr-4">Type</th><th className="py-3 pr-4">Name</th>
                    <th className="py-3 pr-4">Email</th><th className="py-3 pr-4">Phone</th><th className="py-3 pr-4">System</th>
                    <th className="py-3 pr-4">Score</th><th className="py-3 pr-4">Saving</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-[#E2E8F0]/60">
                      <td className="py-3 pr-4 text-[#475569]">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="py-3 pr-4"><Badge variant="outline" className="rounded-full">{l.type}</Badge></td>
                      <td className="py-3 pr-4 font-medium text-[#0B132B]">{l.name || "—"}</td>
                      <td className="py-3 pr-4 text-[#475569]">{l.email || "—"}</td>
                      <td className="py-3 pr-4 text-[#475569]">{l.phone || "—"}</td>
                      <td className="py-3 pr-4 text-[#475569]">{l.current_system || "—"}</td>
                      <td className="py-3 pr-4">
                        <Badge className={`rounded-full ${l.lead_score === "hot" ? "bg-red-100 text-red-700" : l.lead_score === "warm" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}>
                          {l.lead_score || "—"}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 num-tab font-semibold text-[#0B132B]">{l.calculator_results?.annual_saving ? `$${l.calculator_results.annual_saving}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="rounded-2xl p-5 border-[#E2E8F0] bg-white">
      <div className="flex items-center gap-3">
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl bg-[#F8FAFC] ${color}`}><Icon className="h-4 w-4" /></span>
        <div>
          <p className="text-xs uppercase tracking-widest text-[#475569] font-semibold">{label}</p>
          <p className="font-heading text-2xl font-bold text-[#0B132B] num-tab">{value}</p>
        </div>
      </div>
    </Card>
  );
}
